import React, { FC, ReactNode, useState, useMemo, useEffect, useRef } from 'react';
import { FinishData, SAMPLE_PRODUCTS, VariantDefiningAttribute, showsLeadTime } from 'shared/types/product/Product';
import Link from 'components/commercetools-ui/atoms/link';
import PayPalBNPLMessaging from 'components/commercetools-ui/atoms/paypal/PayPalBNPLMessageUI';
import Rating from 'components/commercetools-ui/atoms/rating';
import Typography from 'components/commercetools-ui/atoms/typography';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { inchMark, variantDisplayNames, variantPath } from 'helpers/seo/variant-seo';
import { textToColor } from 'helpers/textToColor/textToColor';
import { getVariantLeadTime } from 'helpers/utils/getVariantLeadTime';
import { replaceTemplateString } from 'helpers/utils/replaceTemplateString';
import { addBusinessDaysExcludingUSHolidays } from 'frontastic/lib/utils/businessDays';
import { ProductDetailsProps } from '..';
import { hasCustomQuoteCta } from '../helpers';
import ConfiguratorStep from './configurator-step';
import FinishSwatches from './finish-swatches';
import PriceSection from './price-section';
import ProductAttribute from './product-attribute';
import VariantSelector from './variant-selector';
import { Wirebrush } from './wirebrush';
import { WoodFinishHelper } from './wood-finish-helper';
import { DEFAULT_LOCALE } from '../../../../../../helpers/constants/locale';
import { ProductJSONLD } from '../../../../../../helpers/structuredData/product';
import { getAxisOptionValues, getSelectableAxes } from '../helpers/axisValues';
import { ALUMINUM_MATERIAL, BRUSHED_FINISH, compatibleFinishOptions } from '../helpers/finishGroups';
import { LEAD_ATTRIBUTE_KEYS } from '../helpers/getVariantSpecies';
import { cleanOptionValue, getAxisLabel, orderAxesForRender } from '../helpers/optionAxes';
import { getProductBuilder } from '../helpers/productBuilder';
import { AxisSelection, findVariantSkuForSelection, normalizeAxisValue, VariantAttributeGroup } from '../helpers/variantMatching';

// Create a reviews link that points to the Reviews component section using a hash anchor
const reviewsLink = '#reviews';

// Extended product interface with dynamic attributeOptions property
interface AttributeOption {
  value: string | number;
  label?: string;
}

interface ProductWithAttributeOptions {
  attributeOptions?: Record<string, AttributeOption[]>;
}

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart' | 'content'> & {
  finish?: FinishData;
  onChangeFinish: (finish: string) => void;
  finishOptions?: string[];
  sheen?: string;
  plainTextDescription?: string;
  onChangeSheen: (sheen: string) => void;
  sheenOptions?: string[];
  origin?: string;
  hasWirebrush?: boolean;
  onChangeWirebrush?: (isChecked: boolean) => void;
  extraCentAmount?: number;
  discountFactor?: number;
  /** Called with `false` when the current option selection resolves to no variant at all. */
  onSelectionMatchChange?: (hasMatchingVariant: boolean) => void;
  /** Pre-computed BNPL messaging amount in cents (product + finish, at current quantity). */
  bnplAmount?: number;
};

// The number an option value starts with, when that number stands alone: "36 in", "36in" and "36-in"
// give 36, but "3/4 in" and "3-1/2 in" give nothing, where parseFloat would read both as 3.
const leadingWholeNumber = (value: string) => {
  const match = /^\s*(\d+(?:\.\d+)?)(?![\d./]|-\d)/.exec(value);
  return match ? Number(match[1]) : Number.NaN;
};

/** Anchor for links into the configurator, e.g. the variant list's "N more options". */
export const PRODUCT_OPTIONS_ID = 'product-options';

const ProductInformation: FC<ProductInformationProps> = ({
  product,
  variant,
  plainTextDescription,
  onChangeVariant,
  inModalVersion,
  wirebrushModal,
  woodFinishHelper,
  finish,
  finishOptions,
  onChangeFinish,
  sheen,
  sheenOptions,
  onChangeSheen,
  hasWirebrush,
  onChangeWirebrush,
  reviewListProps,
  origin,
  extraCentAmount,
  discountFactor,
  onSelectionMatchChange,
  bnplAmount,
}) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  // Remember the last positive BNPL amount so a transient zero/undefined mid-variant-swap keeps
  // the previous message on screen instead of unmounting the banner (which flashes white).
  //
  // The ref is written in an effect rather than during render: render-phase mutation is not safe
  // under StrictMode's double-invoke or concurrent rendering. Behaviour is unchanged, because the
  // ref is only ever *read* on a render where the amount is non-positive — and on those renders
  // the effect would not have written to it anyway.
  const lastBnplAmountRef = useRef(0);
  const positiveBnplAmount = typeof bnplAmount === 'number' && bnplAmount > 0 ? bnplAmount : null;
  useEffect(() => {
    if (positiveBnplAmount !== null) lastBnplAmountRef.current = positiveBnplAmount;
  }, [positiveBnplAmount]);
  const displayBnplAmount = positiveBnplAmount ?? lastBnplAmountRef.current;

  // What the buy box shows as selected. It follows the variant — whichever control changed it, the
  // buy box or the mosaic's color chips — so the buttons always describe the product on screen.
  // Each value is the option string the buttons compare against, matched loosely (case, a `:#hex`
  // suffix, numbers vs "36 in"), so a selection never loses its highlight over formatting.
  // E.g. { length: "3 feet long", plow: "Standard" }
  const productWithOptions = product as typeof product & ProductWithAttributeOptions;
  const variantSelection: AxisSelection = {};
  (product.variantDefiningAttributes ?? []).forEach((att) => {
    const primitive = variant.attributes?.[att.key] as string | number | undefined;
    const options = getAxisOptionValues(product, variant.sku, att);
    const option =
      options.find((value) => normalizeAxisValue(value) === normalizeAxisValue(primitive)) ??
      // A bare number on the variant (36) against a measured option ("36 in").
      (typeof primitive === 'number' ? options.find((value) => leadingWholeNumber(value) === primitive) : undefined);
    variantSelection[att.key] =
      option ?? primitive ?? att.primitiveValues?.[0] ?? productWithOptions.attributeOptions?.[att.key]?.[0]?.value;
  });

  // A pick with no SKU behind it (e.g. a color not made at this height) has no variant to follow,
  // so it's held here until the variant changes, which supersedes it.
  const [pendingPick, setPendingPick] = useState<{ fromSku: string; options: AxisSelection } | null>(null);
  // Superseded means discarded, not hidden: kept, it would resurface when the variant returns to
  // `fromSku` (buy-box Silver, then the Black chip) and show Silver over a Black product.
  if (pendingPick && pendingPick.fromSku !== variant.sku) setPendingPick(null);
  const selectedOptions = pendingPick?.fromSku === variant.sku ? pendingPick.options : variantSelection;

  const [openAttribute, setOpenAttribute] = useState<string | null>(null);

  // Fall back to the variant's own material when material isn't a selectable axis — an
  // Aluminum-only product still offers the brushed finish in its `finishes` attribute, and
  // without this fallback there is no selected material to filter it out against.
  const selectedMaterial = selectedOptions['material'] ?? (variant.attributes?.material as string | undefined);

  // On mount, if the variant SKU encodes Aluminum but the finish query param says Brushed,
  // resolve the conflict by switching to the first non-Brushed finish. Material is baked
  // into the variant SKU so it can't be cleared via local state — the finish query param can.
  // Reads `selectedMaterial`, not `selectedOptions` directly: on an Aluminum-only product the
  // material axis is absent, so the raw lookup is undefined and a deep-linked brushed finish
  // would stay selected while its swatch is hidden.
  useEffect(() => {
    if (selectedMaterial === ALUMINUM_MATERIAL && finish?.name === BRUSHED_FINISH) {
      const firstCompatibleFinish = finishOptions?.find((opt) => textToColor(opt).label !== BRUSHED_FINISH);
      if (firstCompatibleFinish) {
        onChangeFinish(textToColor(firstCompatibleFinish).label);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isBrushedFinishSelected = finish?.name === BRUSHED_FINISH;



  const titleClassName = useClassNames(['break-normal mb-12', { 'body-text-x-large font-medium': !inModalVersion }]);

  const isCustomPdp = hasCustomQuoteCta(product.categories || []);

  // Tread, post and glass-panel PDPs link out to the builder that produces them (null → hidden).
  // Tread family disambiguation reads the product name (a product-level trait) rather than
  // variant.name, which can be a size/SKU label that omits the family keyword.
  const builder = getProductBuilder(product);
  const builderMessage = {
    tread: { id: 'customTreadBuilder', defaultMessage: 'Use the custom tread builder' },
    railing: { id: 'customRailingBuilder', defaultMessage: 'Use the custom railing builder' },
    glass: { id: 'customGlassBuilder', defaultMessage: 'Use the custom glass builder' },
  } as const;

  const leadTime = getVariantLeadTime(variant);
  const shipsOnDate = useMemo(() => {
    if (!leadTime) return null;
    const date = new Date();
    const shipsOnDate = addBusinessDaysExcludingUSHolidays(date, leadTime);
    const weekday = shipsOnDate.toLocaleDateString(undefined, { weekday: 'long' });
    const day = String(shipsOnDate.getDate());
    const month = shipsOnDate.toLocaleDateString(undefined, { month: 'short' });
    return `${weekday}, ${month} ${day}`;
  }, [leadTime]);

  const businessDaysText = formatProductMessage({
    id: 'businessDays',
    defaultMessage: '{leadTime} business day(s)',
  });
  const formattedBusinessDays = replaceTemplateString(businessDaysText, { leadTime });

  // Calculate final price for ProductJSONLD
  const finalBasePrice = variant.price?.centAmount ? variant.price.centAmount + (finish?.price || 0) : 0;

  // Hide "Brushed Stainless" when Aluminum material is selected, and vice versa
  const filteredFinishOptions = compatibleFinishOptions(finishOptions ?? [], selectedMaterial?.toString());

  const getAxisOptions = (attribute: VariantDefiningAttribute) => getAxisOptionValues(product, variant.sku, attribute);

  // Selectable steps only: single-valued attributes are facts about the product, not choices, so
  // they belong in the spec list rather than as a step with one button.
  const selectableAxes = orderAxesForRender(getSelectableAxes(product, variant.sku));

  /**
   * §8: the selection must resolve to a variant that matches on *every* selectable axis. When it
   * doesn't we tell the parent so it can warn and block the add — substituting a near-match would
   * cart a product the shopper never configured.
   */
  const matchedSku = findVariantSkuForSelection(
    product.variantDefiningAttributeGroups as VariantAttributeGroup[] | undefined,
    selectedOptions,
    selectableAxes.map((axis) => axis.key),
  );
  useEffect(() => {
    onSelectionMatchChange?.(matchedSku !== null);
  }, [matchedSku, onSelectionMatchChange]);
  const leadAttributes = selectableAxes.filter((attribute) => LEAD_ATTRIBUTE_KEYS.includes(attribute.key));
  const otherVariantDefiningAttributes = selectableAxes.filter(
    (attribute) => !LEAD_ATTRIBUTE_KEYS.includes(attribute.key),
  );

  const variantNames = variantDisplayNames(product);

  // The SKU an option would select, given the current choices on the other axes: the same matcher
  // the click handler uses, so link, label and selection can never disagree.
  const skuForOption = (attributeKey: string, option: string) =>
    findVariantSkuForSelection(
      product.variantDefiningAttributeGroups as VariantAttributeGroup[] | undefined,
      { ...selectedOptions, [attributeKey]: option },
      selectableAxes.map((axis) => axis.key),
    );

  const renderVariantDefiningAttribute = (attribute: VariantDefiningAttribute) => {
    const productWithOptions = product as typeof product & ProductWithAttributeOptions;
    const optionsToPass = getAxisOptions(attribute);

    const filteredOptions =
      attribute.key === 'material' && isBrushedFinishSelected
        ? optionsToPass.filter((opt) => String(opt) !== ALUMINUM_MATERIAL)
        : optionsToPass;

    // Shopper-facing labels use the inch mark like every name on the page ('36"', not '36 inches');
    // the raw values stay untouched for variant matching. Colours keep their own "Label:#hex" handling.
    const attributeLabels = Object.fromEntries(
      (productWithOptions.attributeOptions?.[attribute.key] ?? []).map((opt) => [String(opt.value), opt.label ?? String(opt.value)]),
    );
    const labelsMap: Record<string, string> = attribute.isColorAttribute
      ? attributeLabels
      : // Every option, not just the visible ones: a value that is filtered out but still selected
        // (Aluminum once Brushed is picked) keeps its curated label.
        Object.fromEntries(optionsToPass.map((opt) => [String(opt), inchMark(attributeLabels[String(opt)] ?? String(opt))]));

    return (
      <ProductAttribute
        key={attribute.key}
        hideLabel
        options={filteredOptions}
        currentVariant={variant}
        attribute={attribute.key}
        attributeLabel={attribute.name}
        isColorAttribute={attribute.isColorAttribute}
        inModalVersion={inModalVersion}
        optionLabels={labelsMap}
        isOpen={openAttribute === attribute.key}
        onOpenChange={(isOpen) => setOpenAttribute(isOpen ? attribute.key : null)}
        selectedOption={String(selectedOptions[attribute.key] ?? '')}
        // Each option links to the variant it would select (same matcher as the click below), so
        // every `/p/<sku>/` page is reachable by a crawler. Options with no exact SKU get no link.
        getOptionHref={(option) => {
          const sku = skuForOption(attribute.key, option);
          return sku && product.slug ? variantPath(product.slug, sku) : undefined;
        }}
        // The link's accessible name is the variant's one display name — the same text the page title,
        // the "More …" list and the structured data use for that URL — not just "Ash" or "42 in".
        getOptionLinkLabel={(option) => {
          const sku = skuForOption(attribute.key, option);
          return sku ? variantNames.get(sku) : undefined;
        }}
        onClick={(attributeKey, option) => {
          // option here is a primitive value (string/number).
          const newSelectedOptions = { ...selectedOptions, [attributeKey]: option };
          setPendingPick({ fromSku: variant.sku, options: newSelectedOptions });
          // Deliberately the *same* matcher (and the same axis keys) that `matchedSku` feeds to
          // `onSelectionMatchChange` → the add-to-cart disabled state. A second, ad-hoc matcher
          // here used to disagree on casing and "Label:#hex" values, so the CTA stayed enabled
          // while `onChangeVariant` never fired and the stale SKU/price got added to the cart.
          const sku = findVariantSkuForSelection(
            product.variantDefiningAttributeGroups as VariantAttributeGroup[] | undefined,
            newSelectedOptions,
            selectableAxes.map((axis) => axis.key),
          );
          // No SKU: keep the selection so the shopper sees what they picked, and let the
          // `matchedSku` effect report no-match so the CTA blocks the add. Silently retaining the
          // previous variant would show a price and SKU the shopper never configured.
          if (sku) onChangeVariant(sku);
          setOpenAttribute(null);
        }}
      />
    );
  };

  const axisSelectedLabel = (attribute: VariantDefiningAttribute) => {
    const selected = selectedOptions[attribute.key];
    if (selected === undefined || selected === '') return null;
    const productWithOptions = product as typeof product & ProductWithAttributeOptions;
    const option = productWithOptions.attributeOptions?.[attribute.key]?.find(
      (opt) => String(opt.value) === String(selected),
    );
    // Some values arrive as `"Label:#hex"` (e.g. Color → "Standard Black:#000000"); the step
    // header shows only the label, matching the swatch's own "Selected: …" text.
    return inchMark(cleanOptionValue(option?.label ?? String(selected)));
  };

  const axisStep = (attribute: VariantDefiningAttribute) => ({
    key: attribute.key,
    label: attribute.name || getAxisLabel(attribute.key),
    selectedValue: axisSelectedLabel(attribute),
    content: renderVariantDefiningAttribute(attribute),
  });

  // A sample product is the sample, so pointing the shopper at "Get a sample" is circular.
  const isSampleProduct = Boolean(product.type && SAMPLE_PRODUCTS.includes(product.type));

  const showFinish = Boolean(product.finishes && filteredFinishOptions.length && finish);
  // Sheen is a wood-only choice — it describes a topcoat, so it has no meaning on metal.
  const showSheen = Boolean(product.isWoodProduct && product.sheens && sheenOptions?.length && sheen);

  /**
   * Step sequence: species/material first (they gate the finish palette and the image), then
   * Finish, then Sheen, then every remaining axis in canonical order. Numbers are assigned from
   * this array's index, so a step's number always matches where it actually renders.
   */
  const steps: { key: string; label: string; selectedValue?: string | null; aside?: ReactNode; content: ReactNode }[] = [
    ...leadAttributes.map(axisStep),
    ...(showFinish
      ? [
          {
            key: 'finish',
            label: 'Finish',
            selectedValue: finish?.name,
            // "Get a sample" sits in the step header, beside the finish name, not under the last swatch group.
            aside:
              !isCustomPdp && !isSampleProduct && woodFinishHelper?.text && woodFinishHelper?.link && woodFinishHelper?.linkLabel ? (
                <WoodFinishHelper {...woodFinishHelper} />
              ) : undefined,
            content: (
              <FinishSwatches
                options={filteredFinishOptions}
                selectedFinishName={finish?.name}
                isWoodProduct={product.isWoodProduct ?? false}
                onSelect={(option) => onChangeFinish(textToColor(option).label)}
                upchargeCents={finish?.price}
                discountFactor={discountFactor}
                currencyCode={variant.price?.currencyCode}
                fractionDigits={variant.price?.fractionDigits}
              />
            ),
          },
        ]
      : []),
    ...(showSheen
      ? [
          {
            key: 'sheen',
            label: 'Sheen',
            selectedValue: sheen,
            content: (
              <VariantSelector
                attribute="sheen"
                options={sheenOptions ?? []}
                selectedOption={sheen}
                isVisual={false}
                onSelect={(option) => onChangeSheen(option)}
              />
            ),
          },
        ]
      : []),
    ...otherVariantDefiningAttributes.map(axisStep),
  ];

  return (
    <div>
      <ProductJSONLD
        product={product}
        variant={variant}
        description={plainTextDescription}
        origin={origin}
        locale={DEFAULT_LOCALE}
        finalBasePrice={
          typeof finalBasePrice === 'number'
            ? finalBasePrice - (finish?.price ?? 0)
            : finalBasePrice
        }
        ratingData={product?.reviewRatingStatistics}
        reviews={reviewListProps}
      />

      <div className="relative flex flex-col">
        {/* The heading is the variant's display name — the same text as <title> and every link to this
            page — so what the page claims matches what search showed. Quick-view keeps it an h2. */}
        <Typography as={inModalVersion ? 'h2' : 'h1'} className={titleClassName}>
          {variantNames.get(variant.sku) ?? variant.name ?? product?.name}
        </Typography>
        <p className="body-text text-tertiary">
          {formatProductMessage({ id: 'sku', defaultMessage: 'SKU' })}: {variant.sku}
        </p>
        {/* Add Rating component if review statistics are available */}
        {product.reviewRatingStatistics?.averageRating && product.reviewRatingStatistics?.count && (
          <div className="mt-8">
            <Rating
              rating={product.reviewRatingStatistics.averageRating}
              reviewCount={product.reviewRatingStatistics.count}
              reviewsLink={reviewsLink}
              textSize="large"
            />
          </div>
        )}
      </div>

      {/* PRICING SECTION — price left, BNPL messaging right on the same row (WEB-2918).
          The banner renders from the last known-good amount, so a transient zero/undefined
          during a variant swap never unmounts it: unmounting is what made it flash white,
          because a remounted PayPal message takes time to paint. */}
      <div className="mb-16 mt-12">
        <div className="flex items-center gap-16">
          <PriceSection
            variant={variant}
            finish={finish}
            extraCentAmount={extraCentAmount}
            className=""
          />
          {displayBnplAmount > 0 && (
            <PayPalBNPLMessaging
              amount={displayBnplAmount}
              placement="product"
              // flex-1/min-w-0 hands the banner the whole remainder of the price row — in a
              // shrink-to-fit box PayPal's iframe wrapped the message at an awkward 75/25 split.
              // The message text itself right-aligns via PayPal's own style option below.
              classNames="ml-auto min-w-0 flex-1"
              style={{ layout: 'text', logo: { type: 'primary' }, text: { align: 'right' } }}
            />
          )}
        </div>

        {/* Full page only, not the quick-view modal: link to the builder that produces this product. */}
        {builder && !inModalVersion && (
          <Link link={builder.href} variant="primary" target="_blank" className="mt-4 inline-flex min-h-44 items-center !text-14 md:min-h-0">
            {formatProductMessage(builderMessage[builder.kind])} →
          </Link>
        )}
      </div>

      {/* `contents` keeps the steps laid out as direct children; the id is the "N more options" target. */}
      <div id={inModalVersion ? undefined : PRODUCT_OPTIONS_ID} className="contents">
        {steps.map((step, index) => (
          <ConfiguratorStep
            key={step.key}
            stepNumber={index + 1}
            label={step.label}
            selectedValue={step.selectedValue}
            aside={step.aside}
          >
            {step.content}
          </ConfiguratorStep>
        ))}
      </div>

      {/* Wirebrushed is an add-on, not a required pick, so it renders last and unnumbered. */}
      {product.useWirebrush && onChangeWirebrush && wirebrushModal && (
        <ConfiguratorStep label="Wirebrushed">
          <Wirebrush
            modal={wirebrushModal}
            checkbox={{
              label: 'Wirebrush',
              name: 'wirebrush',
              checked: hasWirebrush ?? false,
              onChange: ({ checked }) => {
                onChangeWirebrush(checked);
              },
            }}
          />
        </ConfiguratorStep>
      )}

      <div className="mt-24 flex flex-col gap-16">
        {showsLeadTime(product.type) && (
          <div className="flex items-center gap-8">
            {/* eslint-disable-next-line @next/next/no-img-element -- Small decorative icon */}
            <img src="/icons/tailwind-icons/icon-truck.svg" alt="Shipping" className="size-16" />
            <p className="body-text-x-small text-tertiary">
              {shipsOnDate
                ? `${formatProductMessage({ id: 'shipsOn', defaultMessage: 'Ships on' })} ${shipsOnDate}`
                : `${formatProductMessage({ id: 'shipsInAbout', defaultMessage: 'Ships in about' })} ${formattedBusinessDays}`
              }
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProductInformation;
